import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import isEqual from 'fast-deep-equal';
import { produce } from 'immer';
import {
  asyncScheduler,
  BehaviorSubject,
  combineLatest,
  delayWhen,
  filter,
  fromEvent,
  map,
  Observable,
  observeOn,
  of,
  scan,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { SetRequired } from 'type-fest';

import { CanvasElement, RuntimeEventQueryData } from '@blaze-runtime/engine-domain';
import {
  CanvasElementDataProperties,
  CanvasElementPropertiesWithData,
  type ElementFormState,
  ElementQueryState,
  getActiveBinding,
  InputElementMetaUiFeatures,
  makeFontStyles,
  OptionPickerDefaultValue,
  PaginatedComponentModel,
  PaginationDirection,
} from '@blaze-shared/elements-domain';
import { getBackground } from '@blaze-shared/elements-domain';
import { getDataRowFieldValue, selectItemByValue } from '@blaze-shared/schema-domain';
import {
  BlazeSchema,
  createArrayOfObjectsSchemaFromColumns,
  type DatasetRow,
  type ElementSchema,
  isDatasetLinkValue,
  isWorkflowSchema,
  SCHEMA_COLUMNS_LINK,
} from '@blaze-shared/schema-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import {
  countedShareReplay,
  isNullishOrEmptyObject,
  isPrimitive,
  isSelectOption,
  setAlphaToHexColor,
  useEmitTracker,
} from '@blaze-shared/shared-util';
import { isBoolString, isNullish, type Nullish, valueToBool } from '@blaze-shared/shared-util';
import { CssMediaService } from '@blaze-shared/theming-data-access';
import { FALLBACK_THEME } from '@blaze-shared/theming-domain';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { ElementPaginationService } from '../../services/element-pagination.service';
import { RowGenerationService } from '../../services/row-generation.service';
import {
  filterOptionsWithEmptyLabel,
  getFirstOption,
  getLastOption,
  isListSourceBound,
} from '../../utils/canvas-element/bindings.utils';
import { isTimezoneDropdown, updateOptionsFields } from '../../utils/elements/element.utils';
import { CanvasElementBaseInputSelectSingleComponent } from '../__base/base-input-select-single.component';
import { TriggerUpdatePayload } from '../__base/base.component';
import { OptionLabelPipe } from '../__base/option-label.pipe';

import { DropdownSelectedRowService } from './dropdown-row.service';

@UntilDestroy()
@Component({
  selector: 'be-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [OptionLabelPipe, DropdownSelectedRowService],
  standalone: false,
})
export class DropdownComponent
  extends CanvasElementBaseInputSelectSingleComponent<'dropdown'>
  implements CanvasElementComponent<'dropdown'>, OnChanges, OnInit, AfterViewInit, OnDestroy
{
  private readonly cssMediaService = inject(CssMediaService);
  private readonly dropdownRowService = inject(DropdownSelectedRowService);
  private readonly rowGenerationService = inject(RowGenerationService);
  private readonly optionLabelPipe = inject(OptionLabelPipe);
  private readonly elementPaginationService = inject(ElementPaginationService);

  @Input() selected?: DatasetRow;
  @Input() optionsList?: DatasetRow[];
  @Input() formState?: ElementFormState;
  @Input() queryState$?: Observable<ElementQueryState | undefined>;

  @ViewChild('inputContainer') inputContainer!: ElementRef<HTMLDivElement>;
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;
  @ViewChild('filterInput') filterInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MatMenu) menuContainer!: MatMenu;

  @HostBinding('style.--b-dd-accent-color') accentColor = FALLBACK_THEME.value.accent;

  private readonly dataSelected = this.getDataInput('selected');
  private readonly dataOptionsList = this.getDataInput('optionsList');

  private readonly paginationEmitTracker = useEmitTracker();
  private readonly inputEventEmitTracker = useEmitTracker();

  private readonly isInputFocus$$ = new BehaviorSubject<boolean>(false);
  private readonly allLoadedPaginatedOptions$ = new BehaviorSubject<DatasetRow[]>([]);

  private paginationProps: PaginatedComponentModel = {
    state: {
      hasPreviousPage: false,
      hasNextPage: false,
    },
    countOptions: [],
    isServerSide: true,
    enabled: true,
    pageIndex: 0,
    pageSize: 20,
    backwardPaginationSupported: false,
  };

  private isSelectionArray = false;
  private isSelectionLinkValue?: boolean;
  private isSourceBound?: boolean;
  private isSourceClearable?: boolean;
  private paginatedOptionsTotalCount: number | undefined;
  private selectedValue: DatasetRow | undefined;
  private wasEmitted = false;

  readonly elementKeyRoot = 'b-ms-dd';
  readonly elementSizeKeyRoot = 'b-ms-s';

  labelEnabled = signal(false);
  labelText = signal<string>('');
  isReadOnly = signal(false);
  placeholder = signal('');
  prefix = signal('');
  suffix = signal('');

  filterField$$ = new BehaviorSubject('');
  selectedRow$$ = new BehaviorSubject<Nullish<DatasetRow>>(undefined);
  triggerUpdate$$ = new Subject<TriggerUpdatePayload>();

  displayedOptions$ = this.getDisplayedOptions$();

  showNoRowsMessage$ = combineLatest({
    isPending: this.isPending$,
    options: this.displayedOptions$,
  }).pipe(
    map(({ isPending, options }) => {
      if (isPending) {
        return false;
      }

      return options.length === 0 || (options.length === 1 && this.isEmptyRow(options[0]));
    }),
  );

  elementKey?: string;
  elementSizeKey?: string;

  defaultValue: OptionPickerDefaultValue = 'none';

  styles?: Record<string, string>;
  inputWrapperStyles?: Record<string, string>;
  validationStyles?: Record<string, string>;
  valueStyles?: Record<string, string>;
  inputPaddings = 16; // 2* 8px padding
  isTouchMode = this.cssMediaService.isTouchMode;

  get blazeDropdownInfiniteScrollEnabled(): boolean {
    return (
      this.isPaginated &&
      this.paginatedOptionsTotalCount !== undefined &&
      this.paginatedOptionsTotalCount > this.allLoadedPaginatedOptions$.getValue().length
    );
  }

  get isPaginated(): boolean {
    return this.props?.content.menu.dynamicSearch === true;
  }

  get hasValidationError(): boolean | undefined {
    return !!this.formState?.errors?.required || !!this.formState?.errors?.invalid;
  }

  get isMenuOpen(): boolean {
    return !!this.menuTrigger?.menuOpen;
  }

  get displayClearOption(): boolean {
    return (
      !isNullish(this.selectedRow$$.getValue()) ||
      !isNullish(
        typeof this.selectedValue === 'object'
          ? this.selectedValue?.[this.labelField]
          : this.selectedValue,
      )
    );
  }

  constructor() {
    super();

    this.updateSchemasOnChange();

    // Trigger options list and selection update (includes the logic of fetching the selected value when dynamic search is on)
    this.triggerUpdate$$
      .pipe(
        // Reuse the previous empty row if not provided
        scan(
          (acc, updateData) => {
            return {
              emptyRow: updateData.emptyRow ?? acc.emptyRow, // default to previous value
              optionsListOnly: updateData.optionsListOnly ?? false, // always default to false
            };
          },
          { emptyRow: undefined, optionsListOnly: false } as TriggerUpdatePayload,
        ),
        // Empty row is needed to proceed
        filter(
          (updatePayload): updatePayload is SetRequired<TriggerUpdatePayload, 'emptyRow'> =>
            !!updatePayload?.emptyRow,
        ),
        switchMap((updatePayload) => {
          const isSelectedBound = !!getActiveBinding(this.props?.data, 'selected');
          // If dynamic search is not enabled or the schema is not available or not a workflow schema -> ignore the section below
          if (
            !this.props?.content.menu?.dynamicSearch ||
            !isWorkflowSchema(this.dataOptionsList.schema)
          ) {
            return of({ ...updatePayload, selectedRow: undefined });
          }

          if (!isSelectedBound) {
            // `selectedRow: null` means the selection was processed but there is no selection or the value is missing
            this.dropdownRowService.clearSelectedRow();
            return of({ ...updatePayload, selectedRow: null });
          }

          return this.dataSelected.schema$.pipe(
            filter(Boolean),
            switchMap(() => {
              // Only the Workflow scenario is supported for dynamic search
              const datasetId = isWorkflowSchema(this.dataOptionsList.schema)
                ? this.dataOptionsList.schema.tableId
                : undefined;
              const valueColumnName = this.valueField;

              // Sometimes the value is already extracted
              const value =
                typeof this.selectedValue === 'object'
                  ? this.selectedValue?.[valueColumnName]
                  : this.selectedValue;

              // Fetch selected value
              if (datasetId && valueColumnName && value) {
                return this.dropdownRowService
                  .getSelectedRow(datasetId, valueColumnName, value)
                  .pipe(map((selectedRow) => ({ ...updatePayload, selectedRow })));
              } else {
                this.dropdownRowService.clearSelectedRow();
                return of({ ...updatePayload, selectedRow: null });
              }
            }),
          );
        }),
        delayWhen(() => this.getElementEventSchemaSet('change')),
        untilDestroyed(this),
      )
      .subscribe(({ emptyRow, optionsListOnly, selectedRow }) => {
        this.updateOptionsList(emptyRow, selectedRow);
        if (optionsListOnly !== true) {
          this.updateSelection(emptyRow, selectedRow);
        }

        safeDetectChanges(this.cdr);
      });

    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['props']) {
      this.onPropsChange();
    }

    if (changes['selected']) {
      this.processSelection();
    }

    if (changes['props'] || changes['selected'] || changes['optionsList']) {
      const emptyRow = this.getEmptyRow(!!changes['optionsList']);
      this.triggerUpdate$$.next({ emptyRow });
    }
  }

  ngOnInit(): void {
    this.applyElementUiFeatures();

    if (this.elementKey && this.elementSizeKey) {
      this.stylesCleanup.register([this.elementKey, this.elementSizeKey]);
    }

    this.emitUserInput('', true);

    this.applyPaginationProps();
    this.handleOptionsPagination();
  }

  ngAfterViewInit(): void {
    /**
     * Handle clicks outside element / menu
     */
    this.menuTrigger.menuOpened
      .pipe(
        switchMap(() =>
          fromEvent(document.documentElement, 'click').pipe(takeUntil(this.menuTrigger.menuClosed)),
        ),
        untilDestroyed(this),
      )
      .subscribe((event) => {
        const panelElement = document.getElementById(this.menuContainer.panelId);
        const target = event.target as HTMLElement;
        if (this.inputContainer.nativeElement.contains(target) || panelElement?.contains(target)) {
          return;
        }
        this.menuTrigger.closeMenu();
      });

    this.menuTrigger.menuOpened
      .pipe(
        observeOn(asyncScheduler), // wait for the menu to appear on the page
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.isSourceClearable =
          this.defaultValue !== 'first' &&
          this.defaultValue !== 'last' &&
          this.defaultValue !== 'fixedValue';
        const input = this.filterInput?.nativeElement;
        const inputValue = (this.selected || this.selectedValue)?.[this.labelField] ?? '';

        if (input) {
          input.value = isPrimitive(inputValue)
            ? inputValue?.toString() // Make sure it's a string (not a number or boolean, for example)
            : Array.isArray(inputValue)
              ? inputValue.map((v) => (isPrimitive(v) ? v : v.display)).join(', ')
              : inputValue.display;

          // Focus the input after the menu is opened
          input.focus();
          input.select();
        }
      });

    /**
     * Set selected value to input when closing dropdown
     */
    this.menuTrigger.menuClosed.pipe(untilDestroyed(this)).subscribe(() => {
      // On menu close - turn off autocomplete and select value
      this.setInputMode(false);
      this.setFilterInputValue();

      // Refetch user input
      this.emitUserInput(this.filterInput.nativeElement.value);

      window.getSelection()?.removeAllRanges();
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    if (this.elementKey && this.elementSizeKey) {
      this.stylesCleanup.dispose([this.elementKey, this.elementSizeKey]);
    }
  }

  loadNextPage(pageIndex?: number): void {
    pageIndex ??= this.paginationProps.pageIndex + 1;

    const sortConfig = Array.isArray(this.props?.content.sort)
      ? this.props.content.sort[0]
      : this.props?.content.sort;
    const data: RuntimeEventQueryData = {
      sort: {
        columnName: this.schemaService.getColumnName(sortConfig?.field),
        direction: sortConfig?.direction === 'desc' ? 'Descending' : 'Ascending',
      },
      page: {
        size: 20,
        direction: pageIndex === 0 ? PaginationDirection.First : PaginationDirection.Forwards,
        index: pageIndex,
      },
      query: undefined,
      elementId: this.elementId,
      dataSource: this.props?.data?.find((d) => d.key === 'optionsList')?.dataSource,
    };

    if (!this.paginationEmitTracker.shouldEmit(data)) {
      return;
    }

    this.output.emit({
      eventType: 'query',
      data,
      isInitialData: false,
      output: 'pagination',
    });
  }

  onResized(box: DOMRectReadOnly): void {
    this.stylesCleanup.update(
      `${this.elementSizeKey}`,
      `.${this.elementKey} { width: ${box.width + this.inputPaddings}px; }`,
    );
  }

  onSelect(item: DatasetRow): void {
    if (this.isSelectionLinkValue === undefined) {
      this.processSelection();
    }

    this.updateOutput(item);
    this.selectedValue = item;

    // Retrigger the update to reflect manual selection change
    this.triggerUpdate$$.next({ optionsListOnly: true });
  }

  reset(): void {
    const emptyRow = this.getEmptyRow(true);
    this.wasEmitted = false;
    this.outputEmitTracker.reset();
    this.inputEventEmitTracker.reset();
    this.emitUserInput('');
    this.processSelection();
    this.triggerUpdate$$.next({ emptyRow });
    this.cdr.detectChanges();
  }

  setInputMode(inputMode: boolean): void {
    this.isInputFocus$$.next(inputMode);

    // Update input value on lost focus (menu has to be closed)
    if (!inputMode && !this.menuTrigger.menuOpen) {
      this.setFilterInputValue();
      this.emitUserInput(this.filterInput?.nativeElement.value);
    }
  }

  onInputContainerClick(event: MouseEvent): void {
    if (this.isReadOnly()) {
      return;
    }
    const target = event.target as HTMLElement;

    if (!this.isMenuOpen) {
      this.filterField$$.next('');
      this.menuTrigger.openMenu();
    } else if (target !== this.filterInput?.nativeElement) {
      this.menuTrigger.closeMenu();
    }
  }

  onInputKeyDown(event: KeyboardEvent): void {
    // BL-7853 - Temporary solution
    // Pass through only specific events so the FocusKeyManager does not handle every keydown event
    // and take focus, blurring the input in effect.
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      return;
    }

    event.stopPropagation();
  }

  onInputKeyUp(): void {
    this.filterOptions();
  }

  emitUserInput(data: string | undefined, isInitialData = false): void {
    if (this.inputEventEmitTracker.shouldEmit(data)) {
      this.output.emit({
        eventType: 'input',
        data,
        isInitialData,
        schema: this.getEventSchema('input'),
      });

      if (this.isPaginated) {
        this.loadNextPage(0);
      }
    }
  }

  filterOptions(preventClear = false): void {
    const searchInput = this.filterInput?.nativeElement;
    if (this.isSourceClearable && !searchInput?.value.length && !preventClear) {
      this.onSelect({});
    }
    this.filterField$$.next(searchInput?.value?.toLowerCase() || '');
  }

  trackByLabelFn(_: number, item: DatasetRow): string {
    return item[this.labelField];
  }

  private setFilterInputValue(value?: string): void {
    // If the input is focused and dynamic search is used -> don't update
    if (this.props?.content.menu?.dynamicSearch && this.isInputFocus$$.value === true) {
      return;
    }

    this.filterInput.nativeElement.value = isNullish(value)
      ? (this.optionLabelPipe.transform(this.selected, this.labelField, this.defaultValue) ?? '')
      : value;
  }

  private processSelection(): void {
    if (Array.isArray(this.selected)) {
      this.isSelectionArray = true;
      if (this.selected.length) {
        this.selected = this.selected[0];
      }
    }

    if (isDatasetLinkValue(this.selected)) {
      this.isSelectionLinkValue = true;
      this.selected = isDatasetLinkValue(this.selected)
        ? (this.selected?.rowId as any)
        : this.selected;
    }
    this.selectedValue = this.selected;
  }

  private updateOptionsList(emptyRow: DatasetRow, selectedRow?: DatasetRow | null): void {
    const content = this.props?.content;
    const isDynamicSearch = !!content?.menu?.dynamicSearch;

    const isOptionsListReady = Array.isArray(this.optionsList);
    const isSelectedRowDefined = selectedRow !== undefined;

    const shouldBuildOptions = isOptionsListReady && (!isDynamicSearch || isSelectedRowDefined);

    const combinedOptions: DatasetRow[] = [];

    if (shouldBuildOptions) {
      if (selectedRow) {
        combinedOptions.push(selectedRow);
      }
      combinedOptions.push(...(this.optionsList as DatasetRow[]));
    }

    this.displayedOptions = combinedOptions.filter(filterOptionsWithEmptyLabel(this.labelField));

    if (['none', 'placeholder'].includes(this.defaultValue)) {
      this.displayedOptions.unshift({ ...emptyRow });
    }

    this.displayedOptions = this.getUniqueOptions(this.displayedOptions);

    this.filterOptions(true);
    safeDetectChanges(this.cdr);
  }

  private getUniqueOptions(options: DatasetRow[]): DatasetRow[] {
    // Optimization to avoid resolving column ids to names thousands of times
    const valueField = this.valueField;
    const labelField = this.labelField;

    // The data type for the label column is the same for all rows
    const isLabelLink = isDatasetLinkValue(this.displayedOptions[0]?.[labelField]);

    // Keep unique value-label pairs (value can be a nested object)
    // This is an optimized way to find distinct options
    const uniqueRowMap = options.reduce((acc, row: DatasetRow) => {
      const valueString = JSON.stringify(row[valueField]);
      const labelString = isLabelLink ? row[labelField].rowId : row[labelField];
      const key = `${valueString}##⚡️##${labelString}`; // Key is a separated value & label
      if (!acc.has(key)) {
        acc.set(key, row);
      }
      return acc;
    }, new Map<string, DatasetRow>());

    return Array.from(uniqueRowMap.values());
  }

  private updateSelection(emptyRow: DatasetRow, selectedRow?: DatasetRow | null): void {
    const { menu } = this.props?.content ?? {};

    const wasSourceBound = this.isSourceBound;
    const isSourceBound = isListSourceBound(this.props?.data as CanvasElementDataProperties[]);
    if (wasSourceBound !== undefined && wasSourceBound !== isSourceBound) {
      this.selectedValue = undefined;
    }

    // If dynamic search wait for selectedRow to be provided (must not be undefined - can be null)
    const isDynamicSearch = this.props?.content.menu?.dynamicSearch;
    // If options list is present
    // AND in case of dynamic search `selectedRow` info needs to be ready (not undefined)
    const availableOptions =
      Array.isArray(this.optionsList) && (!isDynamicSearch || selectedRow !== undefined)
        ? [...(selectedRow ? [selectedRow] : []), ...this.optionsList]
        : undefined;

    let selected = null;
    if (
      availableOptions?.length &&
      !isNullish(this.selectedValue) &&
      !isEqual(this.selectedValue, emptyRow)
    ) {
      const labelField =
        this.dataSelected.column?.dataType !== 'String' ||
        isTimezoneDropdown({ props: this.props, type: 'dropdown' } as CanvasElement)
          ? undefined
          : this.labelField;
      selected = selectItemByValue(
        availableOptions,
        this.selectedValue,
        this.valueField,
        labelField,
      );
    }

    if (isNullish(selected)) {
      let placeholder = '';
      switch (this.defaultValue) {
        case 'placeholder':
          placeholder = menu?.placeholder ?? '';
          break;
        case 'first':
          selected = getFirstOption(availableOptions, this.valueField);
          break;
        case 'last':
          selected = getLastOption(availableOptions, this.valueField);
          break;
        case 'fixedValue':
          if (menu?.preselect) {
            selected = selectItemByValue(availableOptions, menu?.preselect, this.valueField);
          }
          break;
      }

      this.placeholder.set(placeholder);
    }

    if (availableOptions) {
      this.updateOutput(selected);
    }
  }

  private updateSchemas(
    selectedSchema: BlazeSchema | undefined,
    optionsListSchema: BlazeSchema | undefined,
  ): void {
    // If value is selected but options list data is bound, the schema should return the schema of the bound data source.
    if (selectedSchema && !optionsListSchema) {
      this.setEventSchemaFromDataInput('change', this.dataSelected);
    } else if (optionsListSchema && this.bindingResults?.['optionsList']) {
      if (this.bindingResults?.['selected'] && !selectedSchema) {
        return;
      }

      let eventSchema = this.createElementSchema('change', undefined, optionsListSchema);
      if (
        getActiveBinding(this.props?.data, 'optionsList')?.dataSource?.sourceType ===
          'column-filter' ||
        getActiveBinding(this.props?.data, 'optionsList')?.dataSource?.sourceType ===
          'column-filter-ranged'
      ) {
        eventSchema = produce(eventSchema, (draft) => {
          if (draft?.columns[0]) {
            draft.columns[0].multipleValues = false;
          }
        });
      }

      if (this.dataSelected.column?.columnType === 'Link') {
        this.isSelectionLinkValue = true;
        eventSchema = produce(eventSchema, (draft) => {
          draft?.columns.push(...SCHEMA_COLUMNS_LINK);
        });
      } else if (
        this.dataSelected.column?.multipleValues ||
        this.dataSelected.column?.dataType === 'Array'
      ) {
        this.isSelectionArray = true;
        const optionsArraySchema = createArrayOfObjectsSchemaFromColumns(
          optionsListSchema.name,
          optionsListSchema.columns,
        );
        eventSchema = this.createElementSchema('change', undefined, optionsArraySchema);
      }
      this.setDefaultEventSchema('change', eventSchema);
    } else {
      this.updateDefaultChangeEventSchema();
    }
  }

  private updateDefaultChangeEventSchema(): void {
    let schema: ElementSchema | undefined | null = undefined;
    if (
      !this.bindingResults?.['optionsList'] &&
      this.optionsList?.every((opt) => isBoolString(opt.value))
    ) {
      schema = produce(this.getDefaultEventSchema?.('change'), (draft) => {
        const valueColumn = draft?.columns.find((c) => c.name === 'value');
        if (valueColumn) {
          valueColumn.columnType = 'Boolean';
          valueColumn.dataType = 'Boolean';
        }
      });
    }

    this.setDefaultEventSchema('change', schema);
  }

  private updateOutput(data: DatasetRow | undefined | null): void {
    let isEmpty = false;
    if (!data) {
      this.dropdownRowService.clearSelectedRow();
    }

    if (isNullishOrEmptyObject(data)) {
      isEmpty = true;
      if (isListSourceBound(this.props?.data)) {
        data = this.rowGenerationService.getCachedNewRow(
          this.elementId,
          this.props?.data as CanvasElementDataProperties[],
          false,
        );
      } else {
        data = { label: null, value: null };
      }
    }
    if (!data) {
      throw new Error(`${this.elementId} no data`);
    }
    this.selected = data;
    this.selectedRow$$.next(isEmpty ? undefined : data);
    this.setFilterInputValue();

    const schema = this.getEventSchema('change');
    // handle manually added bool options
    if (
      !this.bindingResults?.['optionsList'] &&
      schema?.columns.find((c) => c.name === 'value')?.dataType === 'Boolean'
    ) {
      data = { value: valueToBool(data.value) };
    }

    // If dynamic search configured - try to update the row cache if rowId is available
    if (this.props?.content.menu?.dynamicSearch && data[this.valueField]) {
      this.dropdownRowService.updateSelectedRow(data, this.valueField);
    }

    if (this.isSelectionLinkValue) {
      // TODO: (override output schema here once it's known what it should be)
      // Include `data` so the columns present in schema are included in the output
      // Ideally the element should never emit the data in this format but it's a workaround for now
      // The output should be just the data row
      data = isNullish(data[this.valueField])
        ? null // Empty link value is null
        : { ...data, rowId: data[this.valueField], display: data[this.labelField] };
    }

    if (this.isSelectionArray) {
      data = [data];
    }
    if (!schema || !this.outputEmitTracker.shouldEmit(data)) {
      return;
    }

    // We consider initial data to be emitted when the first non-undefined non-null version is emitted.
    const isInitialData = !this.wasEmitted && typeof data !== 'undefined' && data !== null;
    this.output.emit({ eventType: 'change', data, schema, isInitialData });
    this.wasEmitted = !!data;
  }

  private getEmptyRow(bustCache = false): DatasetRow {
    if (isListSourceBound(this.props?.data as CanvasElementDataProperties[])) {
      const row = this.rowGenerationService.getCachedNewRow(
        this.elementId,
        this.props?.data as CanvasElementDataProperties[],
        bustCache,
      );
      row[this.labelField] = '🗑clear';
      return row;
    }
    return { [this.labelField]: '🗑clear', [this.valueField]: null };
  }

  private isEmptyRow(option: DatasetRow): boolean {
    const emptyRow = this.getEmptyRow();

    return (
      option[this.labelField] === emptyRow[this.labelField] &&
      option[this.valueField] === emptyRow[this.valueField]
    );
  }

  private getDisplayedOptions$(): Observable<DatasetRow[]> {
    const isDynamicSearch$ = this.props$.pipe(map((props) => props?.content.menu?.dynamicSearch));

    return combineLatest({
      filter: this.filterField$$,
      isDynamicSearch: isDynamicSearch$,
      allLoadedPaginatedOptions: this.allLoadedPaginatedOptions$,
      selectedRow: this.selectedRow$$,
    }).pipe(
      // Don't use front-end filtering if dynamic search is enabled
      map(({ filter, isDynamicSearch, allLoadedPaginatedOptions, selectedRow }) => {
        if (isDynamicSearch && selectedRow) {
          return this.getUniqueOptions([selectedRow, ...allLoadedPaginatedOptions]);
        } else if (isDynamicSearch) {
          return allLoadedPaginatedOptions;
        }

        if (!filter) {
          return this.displayedOptions;
        }

        return this.displayedOptions.filter((option) => {
          const label = getDataRowFieldValue(option, this.labelField);
          return label.toString().toLowerCase().indexOf(filter) > -1;
        });
      }),
      countedShareReplay(),
    );
  }

  private updateSchemasOnChange(): void {
    combineLatest({
      selectedSchema: this.dataSelected.schema$,
      optionsListSchema: this.dataOptionsList.schema$,
    })
      .pipe(
        delayWhen(() => this.elementId$.pipe(filter(Boolean))),
        untilDestroyed(this),
      )
      .subscribe(({ selectedSchema, optionsListSchema }) => {
        this.updateSchemas(selectedSchema, optionsListSchema);
      });
  }

  private onPropsChange(): void {
    if (!this.elementKey) {
      this.elementKey = `${this.elementKeyRoot}-${this.elementId}`;
      this.elementSizeKey = `${this.elementSizeKeyRoot}-${this.elementId}`;
    }

    const { title, menu, data: contentData } = this.props?.content ?? {};

    this.labelEnabled.set(title?.enabled ?? true);

    this.prefix.set(menu?.prefix ?? '');
    this.suffix.set(menu?.suffix ?? '');
    this.defaultValue = contentData?.defaultValue ?? 'none';

    const { data } = (this.props as CanvasElementPropertiesWithData) || {};
    this.isSourceBound = isListSourceBound(data);

    if (this.bindingResults?.['optionsList'] && data?.length) {
      const binding = getActiveBinding(data, 'optionsList');
      if (binding && isSelectOption(binding.bindings)) {
        updateOptionsFields(this, binding.bindings.value as string, binding.bindings.label);
      } else {
        updateOptionsFields(this);
      }
    } else {
      this.optionsList = menu?.values.map((value) => ({ value, label: value })) || [];
      updateOptionsFields(this);
    }

    if (!this.bindingResults?.['optionsList'] && !this.bindingResults?.['selected']) {
      this.updateSchemas(undefined, undefined);
    }
    this.setDefaultEventSchema('input');

    this.updateStyles();
  }

  private applyElementUiFeatures(): void {
    this.getElementUiFeatures<InputElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.labelText.set((features.title ?? '') + this.getRequiredSuffix(features));
        this.isReadOnly.set(features.isReadOnly);
      });
  }

  private handleOptionsPagination(): void {
    this.getDataInput('optionsList')
      .dataInput$.pipe(untilDestroyed(this))
      .subscribe((dataInput) => {
        const shouldResetPaginatedOptions =
          !dataInput?.elementQuery || dataInput.elementQuery.pageIndex === 0;

        if (shouldResetPaginatedOptions && dataInput?.elementQuery?.totalCount) {
          this.paginatedOptionsTotalCount = dataInput.elementQuery.totalCount;
        }

        if (shouldResetPaginatedOptions) {
          this.allLoadedPaginatedOptions$.next([...(dataInput?.data ?? [])]);
        } else {
          this.allLoadedPaginatedOptions$.next(
            this.getUniqueOptions([
              ...this.allLoadedPaginatedOptions$.getValue(),
              ...(dataInput?.data ?? []),
            ]),
          );
        }
      });
  }

  private applyPaginationProps(): void {
    this.queryState$?.pipe(untilDestroyed(this)).subscribe((state) => {
      this.paginationProps = this.elementPaginationService.applyPaginationStateToModel(
        this.paginationProps,
        state,
      );
    });
  }

  private updateStyles(): void {
    const { style, content } = this.props ?? {};
    const { title } = content ?? {};
    const { general, input, list, labelBlock } = style || {};
    if (!style || !title || !general || !input || !list || !labelBlock) {
      return;
    }
    this.accentColor = general.accent.color1;

    const styles: Record<string, string> = {};

    const inputWrapperStyles: Record<string, string> = {
      background: getBackground(input.fill),
      color: input.color.color1,
    };
    const valueStyles: Record<string, string> = {
      ...makeFontStyles(style),
    };
    const validationStyles: Record<string, string> = {};

    if (title.enabled !== false) {
      if (title.position === 'top') {
        styles['flex-direction'] = `column`;
      } else {
        validationStyles['margin-left'] = title.width;
        validationStyles['padding-left'] = '8px';
        inputWrapperStyles['flex-basis'] = `${100 - parseInt(title.width, 10)}%`;
      }
    }

    if (input.border.selectorType !== 'none') {
      inputWrapperStyles['border'] = `1px solid ${input.border.color1}`;
      this.inputPaddings = 18;
    }

    if (input.radius !== '0px') {
      inputWrapperStyles['border-radius'] = input.radius;
    }

    this.styles = styles;
    this.inputWrapperStyles = inputWrapperStyles;
    this.validationStyles = validationStyles;
    this.valueStyles = valueStyles;

    const accent5 = setAlphaToHexColor(general.accent.color1, 5);
    const value50 = setAlphaToHexColor(list.value.color1, 50);

    this.stylesCleanup.update(
      `${this.elementKey}`,
      `
    .${this.elementKey} {
      border-radius: ${list.radius};
      background: ${getBackground(list.background)};
      ${list.border.selectorType !== 'none' ? `border: 1px solid ${list.border.color1};` : ''}
    }

    .${this.elementKey} button.mat-mdc-menu-item {
      color: ${list.value.color1};
      font-size: ${style.fontSize};
      font-family: ${style.fontFamily};
      line-height: ${style.lineHeight};
      border-radius: ${list.radius};
    }
    .${this.elementKey} button:hover {
      color: ${general.accent.color1};
    }
    .${this.elementKey} button.mat-mdc-menu-item.active {
      background: ${accent5};
      color: ${general.accent.color1};
    }
    .${this.elementKey} button.mat-mdc-menu-item.active mat-icon {
      color: ${general.accent.color1};
    }
    .${this.elementKey} button.no-items-option {
      color: ${value50};
    }
    .${this.elementKey} .separator-option {
      border-color: ${list.border.color1};
    }
    `,
    );
  }
}
